import { Box, Typography, Divider, Fab, Zoom, Grid } from "@mui/material";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import arenaIcon from '../icons/arena_icon.png';
import { useState, useEffect } from 'react';
import { useNavigate } from "react-router";
import SocialMediaButton from "./SocialMediaButton";
import { getGoogleDriveMediaUrl } from '../utils/getGoogleDriveMediaUrl';
import SmartImage from '../utils/SmartImage';

export default function Footer({ title, description, site, sponsors = [] }) {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [scrollTimeoutId, setScrollTimeoutId] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }

      if (window.pageYOffset > 200) {
        setShowScrollButton(true);
        const newTimeoutId = setTimeout(() => {
          setShowScrollButton(false);
        }, 3000);
        setScrollTimeoutId(newTimeoutId);
      } else {
        setShowScrollButton(false);
        clearTimeout(scrollTimeoutId);
        setScrollTimeoutId(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutId) {
        clearTimeout(scrollTimeoutId);
      }
    };
  }, [scrollTimeoutId]);

  const returnTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navigate = useNavigate();

  const goTo = (site) => {
    navigate(site);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <Box
      sx={{
        bgcolor: "rgba(94, 106, 108, 0.05)",
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: "auto" ,
        padding: {
          xs: 2,
          md: 2
        },
        minHeight: '200px',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Zoom in={showScrollButton}>
        <Fab
          color="primary"
          size="small"
          aria-label="scroll back to top"
          onClick={returnTop}
          sx={{
            position: 'fixed',
            bottom: { xs: 70, sm: 80, md: 90 },
            right: { xs: 16, sm: 24, md: 32 },
            zIndex: 1000,
            bgcolor: 'grey.600',
            '&:hover': {
              bgcolor: 'grey.700',
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>

      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          mx: {
            xs: 0,
            sm: 0,
            md: '6vw',
            lg: '8vw',
            xl: '12vw',
          },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flexGrow: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'center', md: 'flex-start' },
            marginBottom: 4,
          }}
        >
          {/* Logo */}
          <Box
           onClick={() => goTo('/')}
            sx={{
              display: 'flex',
              height: 120,
              width: 120,
              marginBottom: { xs: 2, md: 0 },
              marginRight: { md: 4 },
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'scale(1.06)',
              },
            }}
          >
            <img src={arenaIcon} alt="Logo" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
          </Box>

          {/* Patrocinadores */}
          <Box
            sx={{
              flexGrow: 1,
              textAlign: 'center',
              marginX: { xs: 0, md: 2 },
              marginBottom: { xs: 2, md: 0 },
              marginTop: 1,
            }}
          >
            {/* <Typography variant="h6" sx={{ mb: 1 }}>
              Nossos Patrocinadores
            </Typography> */}
            {sponsors.length > 0 ? (
              <Grid container spacing={1} justifyContent="center" alignItems="center">
                {sponsors.map((sponsor, index) => (
                  <Grid item xs={6} sm={4} md={3} key={sponsor.id || index}>
                    <Box sx={{ 
                      width: 80, 
                      height: 80,
                      display: 'flex', 
                      justifyContent: 'center',
                      alignItems: 'center',
                      backgroundColor: 'rgba(255, 254, 254, 0.9)',
                      borderRadius: '50%',
                      padding: 0.2,
                      margin: 'auto',
                      overflow: 'hidden'
                    }}>
                      <SmartImage
                        src={getGoogleDriveMediaUrl(
                          sponsor.imageUrl,
                          sponsor.driveFileId,
                          sponsor.webViewLink,
                          sponsor.thumbnailUrl,
                          false
                        )}
                        alt="Patrocinador"
                        driveFileId={sponsor.driveFileId}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '50%',
                          display: 'block'
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                  Sua marca pode estar aqui! Entre em contato e seja um patrocinador oficial da Arena Esporte Total.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Texto à direita */}
          <Box
            sx={{
              textAlign: { xs: 'center', md: 'right' },
              marginLeft: { md: 4 },
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontFamily: "Atlan", fontWeight: "bold", mb: 1 }}
            >
              {title || "Seu Footer"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '400px' }}>
              {description || "Breve descrição sobre o seu site ou empresa."}
            </Typography>
            {site && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Visite nosso site: <a href={site} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{site}</a>
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          mx: {
            xs: 0,
            sm: 0,
            md: '6vw',
            lg: '8vw',
            xl: '12vw',
          },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Divider sx={{ width: '90%', marginBottom: 2, borderColor: 'rgba(0, 0, 0, 0.12)' }} />

        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Ícones de redes sociais */}
          <Box 
            sx={{ 
              display: 'flex', 
              gap: 1,
              marginBottom: { xs: 2, md: 0 },
              order: { xs: 1, md: 2 }
            }}
          >
            <SocialMediaButton iconName="Instagram" hoverColor="purple" link="https://www.instagram.com/amadorarena" />
            <SocialMediaButton iconName="Twitter" link="https://x.com/total_arena" />
            <SocialMediaButton iconName="Facebook" link="https://www.facebook.com/arenaesportetotalgtba" />
          </Box>

          {/* Direitos autorais e política de privacidade */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', sm: 'flex-start' },
              order: { xs: 2, md: 1 },
              marginX: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              &copy; {new Date().getFullYear()} Arena Esporte Total. Todos os direitos reservados.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              onClick={() => goTo('/userLicence')}
              sx={{
                mt: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  color: "steelblue"
                }
              }}
            >
              Política de Privacidade
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}